import { useState, useEffect, useMemo } from "react";

import BuildingProperties from "./object-properties/BuildingProperties";
import AssetProperties from "./object-properties/AssetProperties";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { userAPI } from "./UserAPI";
import { useTranslation } from 'react-i18next';
import LegalPersonProperties from "./object-properties/LegalPersonProperties";
import FloorProperties from "./object-properties/FloorProperties";
import SpaceProperties from "./object-properties/SpaceProperties";
import ContentPanel from "./components/ContentPanel";
import BridgeProperties from "./object-properties/BridgeProperties";
import { getEntityType, Parcel } from "@the-real-insight/tri-model";
import { ApplicationError } from "./general/Errors";
import ParcelProperties from "./object-properties/ParcelProperties";
import GenericEntityView from "./entities/GenericEntityView";
import { knownEntityType } from "./entities/entityTypes";

import { Loader2 } from "lucide-react";
import type { AppShellContext } from "./components/AppShell";
import { Button } from "@/components/ui/button";
import { navigableEntityId } from "./entities/entityNavigation";
import { useEntityTrail } from "./entities/useEntityTrail";
import EntityHierarchyBar from "./entities/EntityHierarchyBar";

/** Warum eine Seite nichts zeigt: Es gibt den Eintrag nicht, oder er kam nicht an. */
type LoadFailure = { kind: 'notFound' | 'failed' | 'unknownType'; detail?: string };

export default function EntityPage() {
    const { t } = useTranslation();
    const { search } = useLocation();
    const navigate = useNavigate();
    const [entity, setEntity] = useState();
    const [loadError, setLoadError] = useState<LoadFailure | null>(null);
    const [loading, setLoading] = useState(true);
    const [attempt, setAttempt] = useState(0);
    const searchParams = useMemo(() => new URLSearchParams(search), [search]);

    // Zwei Adressen für dieselbe Seite: die RESTful `/entities/:type/:id` und die ältere mit Query.
    const params = useParams();
    const entityType = params.entityType ?? searchParams.get("selectedEntityType");
    const rawEntityId = params.entityId ?? searchParams.get("selectedEntity");
    // "undefined" in der Adresse ist eine fehlende Id, kein Eintrag, nach dem man fragen könnte.
    const entityId = navigableEntityId(rawEntityId);

    useEffect(() => {
        const call = async () => {
            if (!entityType) {
                navigate('/chatbot');
            } else if (!knownEntityType(entityType)) {
                setLoadError({ kind: 'unknownType', detail: t('portfolioDashboard.entity.unknownType', { type: entityType }) });
                setLoading(false);
            } else if (!entityId) {
                setEntity(undefined);
                setLoadError({ kind: 'notFound' });
                setLoading(false);
            } else {
                console.log(`Retrieving ${entityType} with ID ${entityId}`);

                setLoading(true);
                setLoadError(null);
                try {
                    // @ts-ignore
                    const newEntity = await userAPI.getEntityByID(entityType, entityId);

                    console.log('Entity found >>>', newEntity);

                    setEntity(newEntity);
                } catch (error) {
                    // 404 heißt: diesen Eintrag gibt es nicht. Alles andere (auch ein abgerissenes
                    // „Failed to fetch") ist ein Ladefehler, den ein zweiter Versuch beheben kann.
                    const notFound = error instanceof ApplicationError && error.status === 404;
                    setLoadError(
                        notFound
                            ? { kind: 'notFound' }
                            : { kind: 'failed', detail: error instanceof Error ? error.message : undefined },
                    );
                    setEntity(undefined);
                } finally {
                    setLoading(false);
                }
            }
        };

        void call();
    }, [entityId, entityType, navigate, t, attempt]);

    const { setTitle } = useOutletContext<AppShellContext>();

    const getTitle = () => {
        if (!entity || !entityType) {
            return '';
        }

        switch (entityType) {
            case 'Parcel':
                return (entity as Parcel).getFullName();
            case 'Space':
                return (entity as { name?: string }).name || '';
            default:
                return getEntityType(entityType)?.type.label.de || '';
        }
    }
    
    const title = getTitle();
    const trail = useEntityTrail(entityType, loadError ? undefined : entity);

    /** Ohne Eintrag kennt die Seite keinen Elternteil — dann zurück, woher man kam. */
    const goBack = () => {
        if (window.history.length > 1) navigate(-1);
        else navigate('/');
    };

    useEffect(() => {
        setTitle(title);
    }, [title, setTitle]);

    return <>
        {loading
            ?
            <div className="flex flex-1 items-center justify-center gap-2 p-10 text-sm text-muted-foreground">
                <Loader2 aria-hidden="true" className="h-5 w-5 animate-spin" />
                {`${t('global.loading')} ...`}
            </div>
            :
            loadError
            ?
            <ContentPanel backgroundColor="transparent">
                <div role="alert" className="mt-4 flex max-w-xl flex-col gap-3">
                    {loadError.kind === 'unknownType'
                        ? <p className="text-sm text-destructive">{loadError.detail}</p>
                        : <>
                            <h2 className="text-lg font-semibold">
                                {t(loadError.kind === 'notFound' ? 'entityPage.notFoundTitle' : 'entityPage.loadFailedTitle')}
                            </h2>
                            <p className="text-sm text-muted-foreground">
                                {t(loadError.kind === 'notFound' ? 'entityPage.notFoundText' : 'entityPage.loadFailedText')}
                            </p>
                            {loadError.kind === 'failed' && loadError.detail
                                ? <p className="text-xs text-muted-foreground">{loadError.detail}</p>
                                : null}
                        </>}
                    <div className="flex flex-wrap gap-2">
                        {loadError.kind === 'failed'
                            ? <Button type="button" onClick={() => setAttempt((n) => n + 1)}>{t('entityPage.retry')}</Button>
                            : null}
                        <Button type="button" variant="outline" onClick={goBack}>{t('entityPage.back')}</Button>
                    </div>
                </div>
            </ContentPanel>
            :
            <ContentPanel backgroundColor="transparent">
                <div className="mb-3">
                    <EntityHierarchyBar trail={trail} />
                </div>
                <div className="entityFillColumn">
                    {(() => {
                        // @ts-ignore
                        switch (entityType) {
                            case 'Asset':
                                return <AssetProperties asset={entity}></AssetProperties>;
                            case 'Floor':
                                return <FloorProperties floor={entity}></FloorProperties>;
                            case 'Space':
                                return <SpaceProperties space={entity}></SpaceProperties>;
                            case 'Building':
                                return <BuildingProperties building={entity}></BuildingProperties>;
                            case 'LegalPerson':
                                return <LegalPersonProperties legalPerson={entity}></LegalPersonProperties>;
                            case 'Bridge':
                                return <BridgeProperties bridge={entity}></BridgeProperties>;
                            case 'Parcel':
                                return <ParcelProperties parcel={entity}></ParcelProperties>;
                            default:
                                // Jeder andere Typ: aus dem Schema angezeigt, zunächst ohne Bearbeiten.
                                return <GenericEntityView entityType={entityType as string} entity={entity}></GenericEntityView>;
                        }
                    })()}
                </div>
            </ContentPanel>
        }
    </>;
}
